import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React from 'react'

import type { Page, Post } from '@/payload-types'
import type { LinkAppearances } from '@/fields/link'
import { ChevronRight } from 'lucide-react'

type CMSLinkType = {
  appearance?: 'inline' | LinkAppearances | null
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  onClick?: () => void
  reference?: {
    relationTo: 'pages' | 'posts'
    value: Page | Post | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
}

const appearanceStyles: Record<LinkAppearances, string> = {
  default: '',
  'outline-solid':
    'bg-transparent text-black border-2 border-black hover:bg-black hover:text-white',
  textlink: '',
  link: 'bg-transparent text-current hover:underline',
  yellow:
    'rounded-lg border border-black bg-accent px-8 py-4 text-xl text-black transition-colors hover:bg-accent/80',
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    onClick,
    reference,
    size: sizeFromProps,
    url,
  } = props

  const href =
    type === 'reference' && typeof reference?.value === 'object' && reference.value.slug
      ? `${reference?.relationTo !== 'pages' ? `/${reference?.relationTo}` : ''}/${
          reference.value.slug
        }`
      : url

  if (!href) return null

  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link className={cn(className)} href={href || url || ''} {...newTabProps} onClick={onClick}>
        {label && label}
        {children && children}
      </Link>
    )
  }

  if (appearance === 'default' || appearance === 'textlink') {
    return (
      <Link
        className={cn(
          'group text-lg font-medium uppercase flex flex-row gap-2 justify-start items-center',
          className,
        )}
        href={href || url || ''}
        {...newTabProps}
        onClick={onClick}
      >
        {label && label}
        {children && children}
        <span className="text-xl transition-all duration-300 group-hover:ml-5">
          <ChevronRight className="ml-2 h-6 w-6" />
        </span>
      </Link>
    )
  }

  // Handle custom appearances
  if (appearance && appearance in appearanceStyles) {
    return (
      <Link
        className={cn(
          'inline-block px-4 py-2 rounded-none transition-colors hover:!underline-none',
          appearanceStyles[appearance as LinkAppearances],
          className,
        )}
        href={href || url || ''}
        {...newTabProps}
        onClick={onClick}
      >
        {label && label}
        {children && children}
      </Link>
    )
  }

  // Fallback to Button component for other appearances
  const size = sizeFromProps || 'default'
  return (
    <Button
      asChild
      className={className}
      size={size}
      variant={appearance as ButtonProps['variant']}
      onClick={onClick}
    >
      <Link href={href || url || ''} {...newTabProps}>
        {label && label}
        {children && children}
      </Link>
    </Button>
  )
}
